import { z } from 'zod';
import { normalizePhoneToE164 } from './phone.ts';
import {
  AFTER_HOURS_OPTIONS,
  RING_TIMEOUT_MAX_SECONDS,
  RING_TIMEOUT_MIN_SECONDS,
  VOICEMAIL_NOTIFY_OPTIONS,
  type AfterHoursPolicy,
  type CallStatus,
  type VoicemailNotify,
} from './voice.ts';

export { AFTER_HOURS_OPTIONS, VOICEMAIL_NOTIFY_OPTIONS, type AfterHoursPolicy, type VoicemailNotify };

/**
 * Settings › Calls and voicemail › Numbers, teams and locations: how each of
 * the company's numbers routes a call. Shared by the Worker
 * (`routes/voice-routing.ts`), the web card and, through the same endpoints,
 * the mobile app (`lib/core/models/voice_routing.dart`).
 *
 * Effective settings on a dialed number resolve number → location → company:
 * a null on the number means "the location's", a null on the location means
 * "the company's". Nothing is merged: a number rings one target.
 */

/** How a number routes a call. `company` is the forwarding phone on the Calls card, as before. */
export const ROUTING_MODES = ['company', 'direct', 'team', 'ivr'] as const;
export type RoutingMode = (typeof ROUTING_MODES)[number];

/** How a team's phones are rung. */
export const TEAM_STRATEGIES = ['simulring', 'sequential', 'round_robin'] as const;
export type TeamStrategy = (typeof TEAM_STRATEGIES)[number];

/** What a menu digit does. */
export const IVR_ACTIONS = ['user', 'team', 'submenu', 'voicemail'] as const;
export type IvrAction = (typeof IVR_ACTIONS)[number];

/** Twilio's `<Dial>` rings at most ten numbers; the team editor stops there. */
export const TEAM_MAX_MEMBERS = 10;
export const VOICE_LABEL_MAX_CHARS = 80;
export const IVR_PROMPT_MAX_CHARS = 500;
/** Digits a menu can use: 0-9. */
export const IVR_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'] as const;

/** The settings a number or a location may override; null = inherit. */
export interface VoiceOverrides {
  ringTimeoutSeconds: number | null;
  recordAnswered: boolean | null;
  voicemailNotify: VoicemailNotify | null;
  afterHours: AfterHoursPolicy | null;
}

/** The same four, resolved. */
export interface VoiceEffective {
  ringTimeoutSeconds: number;
  recordAnswered: boolean;
  voicemailNotify: VoicemailNotify;
  afterHours: AfterHoursPolicy;
}

/** An employee a number or a team can ring. */
export interface VoiceEmployee {
  id: string;
  name: string;
  /** Whether a phone is on their account (a team member without one needs a phone override). */
  hasPhone: boolean;
  active: boolean;
}

export interface VoiceTeamMember {
  userId: string;
  name: string;
  /** Rung instead of their own phone, as E.164, when set. */
  phoneOverride: string | null;
  enabled: boolean;
}

export interface VoiceTeam {
  id: string;
  name: string;
  /** The location the team belongs to; null = company-wide. */
  locationId: string | null;
  strategy: TeamStrategy;
  ringTimeoutSeconds: number | null;
  /** In ring order. */
  members: VoiceTeamMember[];
  /** How many numbers and menu options point at the team (it cannot be deleted while any do). */
  inUseBy: number;
}

export interface VoiceNumber {
  id: string;
  e164: string;
  label: string | null;
  /** `line`: the texting number, which the Calls card binds; `added`: added in this list. */
  kind: 'line' | 'added';
  locationId: string | null;
  mode: RoutingMode;
  targetUserId: string | null;
  targetTeamId: string | null;
  /** Overrides the team's strategy on this number; null = the team's. */
  teamStrategy: TeamStrategy | null;
  ivrMenuId: string | null;
  overrides: VoiceOverrides;
  effective: VoiceEffective;
}

export interface VoiceLocationDefaults {
  locationId: string;
  name: string;
  overrides: VoiceOverrides;
}

export interface IvrOption {
  id: string;
  digit: string;
  label: string;
  action: IvrAction;
  targetUserId: string | null;
  targetTeamId: string | null;
  submenuId: string | null;
}

export interface IvrMenu {
  id: string;
  name: string;
  promptText: string;
  options: IvrOption[];
  /** Numbers and menu options pointing at this menu. */
  inUseBy: number;
}

/** A number on the company's own Twilio account that is not in the list yet. */
export interface AccountNumber {
  e164: string;
  friendlyName: string | null;
  /** Whether Twilio says it can take calls. */
  voice: boolean;
}

/** Everything the Numbers, teams and locations screens show, in one read. */
export interface VoiceRoutingView {
  /** Whether this installation answers calls at all (saving is refused otherwise). */
  available: boolean;
  companyDefaults: VoiceEffective;
  numbers: VoiceNumber[];
  teams: VoiceTeam[];
  ivrMenus: IvrMenu[];
  locations: VoiceLocationDefaults[];
  employees: VoiceEmployee[];
  /**
   * Whether numbers can be added from the company's own Twilio account: it
   * is connected and switched on. A number BlastoClean issued is the one
   * `line` row and cannot be added to.
   */
  canAddNumbers: boolean;
}

/** One call in the log: which number, where, when, how long, and who took it. */
export interface VoiceCallLogEntry {
  id: string;
  numberId: string | null;
  to: string;
  from: string;
  locationId: string | null;
  startedAt: string;
  status: CallStatus;
  durationSeconds: number | null;
  voicemail: boolean;
  /** Whether a phone it rang was answered (the inbound leg's status cannot say). */
  answered: boolean;
  ringTarget: 'forward' | 'user' | 'team' | 'ivr' | 'voicemail' | null;
  teamId: string | null;
  answeredByUserId: string | null;
  answeredByName: string | null;
}

// ---------------------------------------------------------------------------
// What the owner saves
// ---------------------------------------------------------------------------

const ringTimeout = z.number().int().min(RING_TIMEOUT_MIN_SECONDS).max(RING_TIMEOUT_MAX_SECONDS);
const id = z.string().trim().min(1).max(64);
const optionalId = id.nullable().optional();

const overridesSchema = z
  .object({
    ringTimeoutSeconds: ringTimeout.nullable(),
    recordAnswered: z.boolean().nullable(),
    voicemailNotify: z.enum(VOICEMAIL_NOTIFY_OPTIONS).nullable(),
    afterHours: z.enum(AFTER_HOURS_OPTIONS).nullable(),
  })
  .strict();

/** A phone in any format a person types, stored as E.164 or refused. */
const phone = z
  .string()
  .trim()
  .max(32)
  .transform((value, ctx) => {
    const normalized = normalizePhoneToE164(value);
    if (!normalized) {
      ctx.addIssue({ code: 'custom', message: 'Enter a phone number that can take calls.' });
      return z.NEVER;
    }
    return normalized;
  });

export const addVoiceNumberSchema = z
  .object({ phoneNumber: phone, label: z.string().trim().max(VOICE_LABEL_MAX_CHARS).optional() })
  .strict();
export type AddVoiceNumberInput = z.infer<typeof addVoiceNumberSchema>;

/**
 * A number's routing. The target of the chosen mode is required and the
 * others are ignored (and cleared): a number rings one thing.
 */
export const updateVoiceNumberSchema = z
  .object({
    label: z
      .string()
      .trim()
      .max(VOICE_LABEL_MAX_CHARS)
      .nullable()
      .transform((value) => value || null),
    locationId: id.nullable(),
    mode: z.enum(ROUTING_MODES),
    targetUserId: optionalId,
    targetTeamId: optionalId,
    teamStrategy: z.enum(TEAM_STRATEGIES).nullable().optional(),
    ivrMenuId: optionalId,
    overrides: overridesSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.mode === 'direct' && !value.targetUserId)
      ctx.addIssue({ code: 'custom', path: ['targetUserId'], message: 'Choose the employee this number rings.' });
    if (value.mode === 'team' && !value.targetTeamId)
      ctx.addIssue({ code: 'custom', path: ['targetTeamId'], message: 'Choose the team this number rings.' });
    if (value.mode === 'ivr' && !value.ivrMenuId)
      ctx.addIssue({ code: 'custom', path: ['ivrMenuId'], message: 'Choose the menu this number plays.' });
  });
export type UpdateVoiceNumberInput = z.infer<typeof updateVoiceNumberSchema>;

export const saveVoiceTeamSchema = z
  .object({
    name: z.string().trim().min(1, 'Name the team.').max(VOICE_LABEL_MAX_CHARS),
    locationId: id.nullable(),
    strategy: z.enum(TEAM_STRATEGIES),
    ringTimeoutSeconds: ringTimeout.nullable(),
    members: z
      .array(
        z
          .object({
            userId: id,
            phoneOverride: phone.nullable().optional(),
            enabled: z.boolean().optional(),
          })
          .strict(),
      )
      .max(TEAM_MAX_MEMBERS, `A team rings at most ${TEAM_MAX_MEMBERS} phones.`)
      .superRefine((members, ctx) => {
        const seen = new Set<string>();
        for (const member of members) {
          if (seen.has(member.userId)) ctx.addIssue({ code: 'custom', message: 'An employee is listed twice.' });
          seen.add(member.userId);
        }
      }),
  })
  .strict();
export type SaveVoiceTeamInput = z.infer<typeof saveVoiceTeamSchema>;

export const saveLocationVoiceSchema = overridesSchema;
export type SaveLocationVoiceInput = z.infer<typeof saveLocationVoiceSchema>;

export const saveIvrMenuSchema = z
  .object({
    name: z.string().trim().min(1, 'Name the menu.').max(VOICE_LABEL_MAX_CHARS),
    promptText: z.string().trim().min(1, 'Write what the caller hears.').max(IVR_PROMPT_MAX_CHARS),
    options: z
      .array(
        z
          .object({
            digit: z.enum(IVR_DIGITS),
            label: z.string().trim().min(1).max(VOICE_LABEL_MAX_CHARS),
            action: z.enum(IVR_ACTIONS),
            targetUserId: optionalId,
            targetTeamId: optionalId,
            submenuId: optionalId,
          })
          .strict()
          .superRefine((option, ctx) => {
            if (option.action === 'user' && !option.targetUserId)
              ctx.addIssue({ code: 'custom', path: ['targetUserId'], message: 'Choose the employee.' });
            if (option.action === 'team' && !option.targetTeamId)
              ctx.addIssue({ code: 'custom', path: ['targetTeamId'], message: 'Choose the team.' });
            if (option.action === 'submenu' && !option.submenuId)
              ctx.addIssue({ code: 'custom', path: ['submenuId'], message: 'Choose the menu.' });
          }),
      )
      .max(IVR_DIGITS.length)
      .superRefine((options, ctx) => {
        const seen = new Set<string>();
        for (const option of options) {
          if (seen.has(option.digit)) ctx.addIssue({ code: 'custom', message: `Digit ${option.digit} is used twice.` });
          seen.add(option.digit);
        }
      }),
  })
  .strict();
export type SaveIvrMenuInput = z.infer<typeof saveIvrMenuSchema>;

export const voiceCallLogQuerySchema = z
  .object({
    numberId: id.optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
  })
  .strict();

/** Resolves number → location → company, field by field. */
export function resolveVoiceEffective(
  company: VoiceEffective,
  location: VoiceOverrides | null,
  number: VoiceOverrides | null,
): VoiceEffective {
  const pick = <K extends keyof VoiceEffective>(key: K): VoiceEffective[K] =>
    (number?.[key] ?? location?.[key] ?? company[key]) as VoiceEffective[K];
  return {
    ringTimeoutSeconds: pick('ringTimeoutSeconds'),
    recordAnswered: pick('recordAnswered'),
    voicemailNotify: pick('voicemailNotify'),
    afterHours: pick('afterHours'),
  };
}

/**
 * The order a team's phones are tried in for one call: as listed for
 * simulring and sequential; rotated by the cursor for round-robin, so each
 * call starts one member later than the last.
 */
export function ringOrder<T>(members: readonly T[], strategy: TeamStrategy, cursor: number): T[] {
  if (strategy !== 'round_robin' || members.length === 0) return [...members];
  const start = ((cursor % members.length) + members.length) % members.length;
  return [...members.slice(start), ...members.slice(0, start)];
}
